/**
 * TriageSelector — modal que pide al alumno asignar el triage del paciente
 * ANTES de abrir el diferencial. Replica el flujo real: la decisión de
 * gravedad (rojo/ámbar/verde/negro) se toma en ≤60s sin conocer el dx
 * exacto, después viene el razonamiento más profundo.
 *
 * Flujo:
 *   1. Alumno clickea "Panel diferencial" (o es redirigido desde Assistant).
 *   2. Si state.userProposedTriage === null, aparece este selector primero.
 *   3. Alumno elige 1 de 5 colores → state.userProposedTriage = opción.
 *   4. Feedback inmediato: correcto (verde) o explicación.
 *   5. Continúa al DifferentialPanel.
 */

import state from '../core/state';
import type { TriagePriority } from '../types/medical';

const OVERLAY_ID = 'cgt-triage-selector';
const STYLE_ID = 'cgt-triage-selector-styles';

interface TriageOption {
  value: TriagePriority;
  label: string;
  sub: string;
  color: string;
  description: string;
}

const OPTIONS: TriageOption[] = [
  {
    value: 'immediate',
    label: 'INMEDIATO',
    sub: 'Código rojo',
    color: '#FF453A',
    description: 'Riesgo vital — requiere atención YA. ABC comprometido, shock, IAM, hemorragia severa.',
  },
  {
    value: 'delayed',
    label: 'PUEDE ESPERAR',
    sub: 'Código ámbar',
    color: '#FF9F0A',
    description: 'Grave pero estable. Fractura compleja, dolor abdominal, neumonía. Evaluación en <30 min.',
  },
  {
    value: 'minor',
    label: 'AMBULATORIO',
    sub: 'Código verde',
    color: '#30D158',
    description: 'Estable, sin riesgo vital. Esguinces, faringitis, heridas menores.',
  },
  {
    value: 'expectant',
    label: 'EXPECTANTE',
    sub: 'Código gris',
    color: '#8E8E93',
    description: 'Daño muy severo, pronóstico malo con recursos limitados. Cuidados paliativos.',
  },
  {
    value: 'deceased',
    label: 'FALLECIDO',
    sub: 'Código negro',
    color: '#3A3A3C',
    description: 'Sin signos vitales o daño incompatible con la vida.',
  },
];

export class TriageSelector {
  private root: HTMLElement | null = null;

  /**
   * Muestra el selector. `onResolved` se llama SIEMPRE al cerrar (con o sin
   * elección) para que el caller continúe con el siguiente paso (diferencial).
   * Si `state.userProposedTriage` ya está seteado, se skippea y llama al
   * callback directo (idempotente).
   */
  show(expected: TriagePriority, onResolved: () => void): void {
    if (state.userProposedTriage !== null) {
      onResolved();
      return;
    }
    if (this.root) return;
    this.injectStyles();

    const root = document.createElement('div');
    root.id = OVERLAY_ID;
    root.innerHTML = `
      <div class="cgt-tri-backdrop"></div>
      <article class="cgt-tri-card">
        <header>
          <div class="cgt-tri-eyebrow">PASO 1 DE 2 · Triage</div>
          <h2>¿Qué gravedad tiene este paciente?</h2>
          <p>Basado en lo que viste en el grafo, asigná la gravedad del paciente. Tenés que hacerlo rápido (en triage real, ≤60 segundos).</p>
        </header>
        <div class="cgt-tri-options">
          ${OPTIONS.map(o => `
            <button type="button" class="cgt-tri-option" data-triage="${o.value}" style="--accent:${o.color}">
              <span class="cgt-tri-chip" style="background:${o.color}"></span>
              <div class="cgt-tri-text">
                <strong>${o.label}</strong>
                <small>${o.sub}</small>
                <p>${o.description}</p>
              </div>
            </button>
          `).join('')}
        </div>
        <footer data-footer></footer>
      </article>
    `;

    root.querySelectorAll<HTMLButtonElement>('[data-triage]').forEach(btn => {
      btn.addEventListener('click', () => {
        const chosen = btn.dataset.triage as TriagePriority;
        this.handleChoice(chosen, expected, onResolved);
      });
    });

    document.body.appendChild(root);
    requestAnimationFrame(() => root.classList.add('cgt-tri-visible'));
    this.root = root;
  }

  private handleChoice(chosen: TriagePriority, expected: TriagePriority, onResolved: () => void): void {
    state.userProposedTriage = chosen;
    const footer = this.root?.querySelector<HTMLElement>('[data-footer]');
    if (!footer) return;
    const correct = chosen === expected;
    const chosenOpt = OPTIONS.find(o => o.value === chosen);
    const expectedOpt = OPTIONS.find(o => o.value === expected);
    footer.innerHTML = correct
      ? `
        <div class="cgt-tri-feedback cgt-tri-ok">
          <strong>✓ Triage correcto</strong>
          <p>Asignaste <span style="color:${chosenOpt?.color}">${chosenOpt?.label}</span> y eso es lo que correspondía. Sigamos con el diagnóstico.</p>
        </div>
        <button type="button" class="cgt-tri-continue" data-continue>Ir al diferencial →</button>
      `
      : `
        <div class="cgt-tri-feedback cgt-tri-wrong">
          <strong>✗ Triage distinto al esperado</strong>
          <p>Elegiste <span style="color:${chosenOpt?.color}">${chosenOpt?.label}</span>. El triage oficial del caso es <span style="color:${expectedOpt?.color}">${expectedOpt?.label}</span>.</p>
          <small>Revisá los signos vitales y el contexto antes de seguir. El puntaje del triage queda registrado, podés continuar con el dx.</small>
        </div>
        <button type="button" class="cgt-tri-continue" data-continue>Ir al diferencial →</button>
      `;
    // Desactivar resto de opciones para marcar la elección
    this.root?.querySelectorAll<HTMLButtonElement>('[data-triage]').forEach(btn => {
      const isChosen = btn.dataset.triage === chosen;
      btn.disabled = true;
      btn.style.opacity = isChosen ? '1' : '0.35';
      if (isChosen && !correct) {
        btn.style.borderColor = '#FF453A';
      }
    });
    footer.querySelector<HTMLButtonElement>('[data-continue]')?.addEventListener('click', () => {
      this.close();
      onResolved();
    });
  }

  close(): void {
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-tri-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${OVERLAY_ID} {
        position: fixed; inset: 0; z-index: 13500;
        display: flex; align-items: center; justify-content: center;
        opacity: 0; transition: opacity 0.25s;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
      }
      #${OVERLAY_ID}.cgt-tri-visible { opacity: 1; }
      #${OVERLAY_ID} .cgt-tri-backdrop {
        position: absolute; inset: 0;
        background: radial-gradient(circle at 50% 30%, rgba(20,20,40,0.9) 0%, rgba(0,0,0,0.95) 80%);
        backdrop-filter: blur(8px);
      }
      #${OVERLAY_ID} .cgt-tri-card {
        position: relative; width: min(640px, 94vw); max-height: 88vh;
        background: #0a0a0a; border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 18px; padding: 26px 32px 22px;
        color: #f5f5f7; overflow-y: auto;
        box-shadow: 0 24px 80px rgba(0,0,0,0.6);
      }
      #${OVERLAY_ID} header { margin-bottom: 16px; }
      #${OVERLAY_ID} .cgt-tri-eyebrow {
        font-size: 10.5px; font-weight: 700; letter-spacing: 1.5px;
        text-transform: uppercase; color: #2997FF; margin-bottom: 8px;
      }
      #${OVERLAY_ID} h2 {
        font-size: 20px; font-weight: 700; letter-spacing: -0.3px;
        margin-bottom: 6px;
      }
      #${OVERLAY_ID} header p { font-size: 13px; color: #a1a1a6; line-height: 1.5; }

      #${OVERLAY_ID} .cgt-tri-options {
        display: flex; flex-direction: column; gap: 8px;
      }
      #${OVERLAY_ID} .cgt-tri-option {
        display: flex; gap: 14px; align-items: flex-start;
        background: rgba(255,255,255,0.02);
        border: 0.5px solid rgba(255,255,255,0.06);
        border-left: 3px solid var(--accent);
        border-radius: 10px; padding: 12px 14px;
        color: #f5f5f7; cursor: pointer; text-align: left;
        font-family: inherit;
        transition: background 0.15s, transform 0.1s;
      }
      #${OVERLAY_ID} .cgt-tri-option:hover:not(:disabled) {
        background: color-mix(in srgb, var(--accent) 15%, rgba(255,255,255,0.02));
        transform: translateX(2px);
      }
      #${OVERLAY_ID} .cgt-tri-option:disabled { cursor: default; }
      #${OVERLAY_ID} .cgt-tri-chip {
        width: 14px; height: 14px; border-radius: 50%; flex-shrink: 0;
        margin-top: 3px;
        box-shadow: 0 0 10px var(--accent);
      }
      #${OVERLAY_ID} .cgt-tri-text { flex: 1; min-width: 0; }
      #${OVERLAY_ID} .cgt-tri-text strong {
        display: block; font-size: 14px; font-weight: 700; letter-spacing: 0.3px;
      }
      #${OVERLAY_ID} .cgt-tri-text small {
        display: block; font-size: 10.5px; color: #8e8e93;
        text-transform: uppercase; letter-spacing: 0.5px;
        margin: 2px 0 6px;
      }
      #${OVERLAY_ID} .cgt-tri-text p {
        font-size: 12px; color: #a1a1a6; line-height: 1.5; margin: 0;
      }

      #${OVERLAY_ID} footer { margin-top: 16px; }
      #${OVERLAY_ID} .cgt-tri-feedback {
        padding: 12px 14px; border-radius: 10px; margin-bottom: 12px;
      }
      #${OVERLAY_ID} .cgt-tri-ok {
        background: rgba(48,209,88,0.08);
        border: 0.5px solid rgba(48,209,88,0.3);
      }
      #${OVERLAY_ID} .cgt-tri-wrong {
        background: rgba(255,69,58,0.08);
        border: 0.5px solid rgba(255,69,58,0.3);
      }
      #${OVERLAY_ID} .cgt-tri-feedback strong {
        display: block; font-size: 13px; margin-bottom: 4px;
      }
      #${OVERLAY_ID} .cgt-tri-feedback p {
        font-size: 12px; color: #d1d1d6; line-height: 1.5; margin: 0;
      }
      #${OVERLAY_ID} .cgt-tri-feedback small {
        display: block; font-size: 11px; color: #8e8e93; margin-top: 8px;
      }
      #${OVERLAY_ID} .cgt-tri-continue {
        width: 100%; padding: 12px;
        background: linear-gradient(135deg, #2997FF, #BF5AF2);
        color: #fff; border: none; border-radius: 10px;
        font-size: 14px; font-weight: 700; cursor: pointer; font-family: inherit;
      }
      #${OVERLAY_ID} .cgt-tri-continue:hover { filter: brightness(1.1); }
    `;
    document.head.appendChild(style);
  }
}

export const triageSelector = new TriageSelector();
